/**
 * SDA-267: campaign sign-up attribution.
 *
 * Campaign email CTA links land with `utm_source=sda-campaign&sda_c=<campaign
 * uuid>[&sda_ct=<contact uuid>][&utm_campaign=<slug>]`. The params only exist
 * in the URL for the first page load (EventSearchPage rewrites the query
 * string on re-search), so main.tsx captures them into localStorage BEFORE
 * React renders. Sign-up API calls (register, activate-dormant, booking,
 * inquiries, waitlist) read the stored blob at submit time and attach it
 * out-of-band; the server validates and stamps it — a missing/garbled blob
 * never blocks anything.
 *
 * Storage is last-touch (a newer campaign landing overwrites) with a 30-day
 * TTL, and is deliberately NOT cleared after a sign-up: one campaign may drive
 * an activation AND a later booking.
 */

const STORAGE_KEY = 'sda_attribution';
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface StoredAttribution {
  campaign_id: string;
  contact_id?: string;
  utm_source?: string;
  utm_campaign?: string;
  landed_at: string;
}

/**
 * Capture campaign attribution params from a landing URL into localStorage.
 * Call once, synchronously, before React renders. No-ops (and never throws)
 * when params are absent/invalid or storage is unavailable (private mode).
 */
export function captureAttributionFromUrl(search: string): void {
  try {
    const params = new URLSearchParams(search);
    const campaignId = params.get('sda_c');
    if (!campaignId || !UUID_RE.test(campaignId)) return;

    const contactId = params.get('sda_ct');
    const utmSource = params.get('utm_source');
    const utmCampaign = params.get('utm_campaign');

    const blob: StoredAttribution = {
      campaign_id: campaignId,
      ...(contactId && UUID_RE.test(contactId) ? { contact_id: contactId } : {}),
      ...(utmSource ? { utm_source: utmSource.slice(0, 100) } : {}),
      ...(utmCampaign ? { utm_campaign: utmCampaign.slice(0, 150) } : {}),
      landed_at: new Date().toISOString(),
    };
    // Last-touch: a newer campaign landing overwrites any previous one.
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(blob));
  } catch {
    // Storage unavailable or malformed URL — attribution is best-effort only.
  }
}

/**
 * Read the stored attribution blob. Returns null (and clears storage) when
 * absent, unparseable, structurally invalid, or older than 30 days.
 */
export function getAttribution(): StoredAttribution | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredAttribution> | null;
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      typeof parsed.campaign_id !== 'string' ||
      !UUID_RE.test(parsed.campaign_id) ||
      typeof parsed.landed_at !== 'string'
    ) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    const landedAt = Date.parse(parsed.landed_at);
    if (Number.isNaN(landedAt) || Date.now() - landedAt > TTL_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed as StoredAttribution;
  } catch {
    return null;
  }
}
